import { useState, useContext, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getDeliveryBoys, createDeliveryBoy } from "../../api/adminApi";
import { AuthContext } from "../../context/AuthContext";

import {
  Typography, Button, Dialog, DialogTitle, DialogContent, TextField,
  DialogActions, Card, CardContent, CircularProgress, Box, Stack,
  Avatar, Snackbar, Alert, Chip, IconButton
} from "@mui/material";

import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export default function CityRiders({ zonalAdminId }) {
  const { admin } = useContext(AuthContext);
  const navigate = useNavigate();

  const { id: urlZonalAdminId } = useParams();
  const activeAdminId = zonalAdminId || urlZonalAdminId;

  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [openAdd, setOpenAdd] = useState(false);

  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", zone: "" });

  const showMessage = (msg, type = "success") => setSnackbar({ open: true, message: msg, severity: type });

  const fetchRiders = useCallback(async () => {
    try {
      setLoading(true);
      const params = activeAdminId ? { zonal_admin_id: activeAdminId } : {};
      const res = await getDeliveryBoys(params);
      setRiders(res.data);
    } catch (err) {
      showMessage("Failed to load riders", "error");
    } finally {
      setLoading(false);
    }
  }, [activeAdminId]);

  useEffect(() => {
    if (admin?.role === "CITY_ADMIN" || admin?.role === "ZONAL_ADMIN") fetchRiders();
  }, [admin, fetchRiders]);

  const handleOpenAdd = () => {
    setForm({ name: "", email: "", phone: "", password: "", zone: "" });
    setOpenAdd(true);
  };

  const handleAddRider = async () => {
    try {
      setActionLoading(true);
      const payload = { ...form };
      if (activeAdminId) payload.zonal_admin_id = activeAdminId;

      await createDeliveryBoy(payload);
      setOpenAdd(false);
      fetchRiders();
      showMessage("Rider created successfully!");
    } catch (err) {
      showMessage(err?.response?.data?.detail || "Failed to create rider", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const glassCardStyle = {
    borderRadius: '24px', border: "1px solid #ECE5DA", bgcolor: '#FFFFFF',
    boxShadow: '0 10px 24px rgba(0, 0, 0, 0.05)', cursor: 'pointer',
    transition: 'all 0.2s ease', '&:hover': { transform: 'translateY(-3px)', borderColor: '#F8C332' }
  };

  return (
    <Box sx={{ pb: 10 }}>
      {/* 🚀 HEADER WITH "ADD RIDER" BUTTON */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              bgcolor: '#FFFFFF',
              border: '1px solid #ECE5DA',
              color: '#3A2C23',
              '&:hover': { bgcolor: '#F8C332', borderColor: '#F8C332' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight="900" sx={{ color: "#3A2C23", letterSpacing: "-0.5px" }}>
            Zonal Riders
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
          sx={{ borderRadius: "14px", bgcolor: "#F8C332", color: "#3A2C23", fontWeight: "900", py: 1.2, px: 3, boxShadow: "0 4px 12px rgba(248, 195, 50, 0.3)", "&:hover": { bgcolor: "#F5B71D" } }}
        >
          Add Rider
        </Button>
      </Box>

      {/* LIST OF RIDERS */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
          <CircularProgress sx={{ color: "#F8C332" }} />
        </Box>
      ) : riders.length === 0 ? (
        <Box textAlign="center" py={5} color="#A59D96">
          <LocalShippingIcon sx={{ fontSize: 60, opacity: 0.5, mb: 1 }} />
          <Typography variant="h6" fontWeight="700">No Riders Found</Typography>
        </Box>
      ) : (
        <Stack spacing={2} mb={4}>
          {riders.map((r) => (
            <Card
              key={r.id}
              onClick={() => navigate(urlZonalAdminId ? `/zonal-admins/${urlZonalAdminId}/riders/${r.id}` : `/riders/${r.id}`, { state: { rider: r } })}
              sx={glassCardStyle}
              elevation={0}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 }, "&:last-child": { pb: { xs: 2, sm: 3 } } }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>

                  <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 2 }, alignItems: "center", flex: 1, minWidth: 0 }}>
                    <Avatar sx={{ bgcolor: "#FFF6DE", color: "#F29D17", width: 50, height: 50, fontWeight: "900", fontSize: "1.2rem" }}>
                      {r.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="h6" fontWeight="900" sx={{ color: "#3A2C23", lineHeight: 1.2, textTransform: 'capitalize' }} noWrap>
                          {r.name}
                        </Typography>
                        <Chip label={r.status || "IDLE"} size="small" sx={{ fontWeight: '800', fontSize: '0.65rem', height: '20px', bgcolor: r.status === 'IDLE' ? '#E1EED3' : r.status === 'BUSY' ? '#FFF6DE' : '#FEE2E2', color: r.status === 'IDLE' ? '#577A34' : r.status === 'BUSY' ? '#D97706' : '#DC2626' }} />
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#6E655E", mt: 0.5 }}>
                        <EmailOutlinedIcon sx={{ fontSize: 16 }} />
                        <Typography variant="body2" fontWeight="600" noWrap>{r.email}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#6E655E", mt: 0.3 }}>
                        <PhoneOutlinedIcon sx={{ fontSize: 16 }} />
                        <Typography variant="body2" fontWeight="600" noWrap>{r.phone || "N/A"}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3, color: "#A59D96" }}>
                        <LocationOnOutlinedIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption" fontWeight="800" sx={{ textTransform: "uppercase" }} noWrap>
                          Zone: {r.zone || "Unassigned"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Right arrow indicator */}
                  <ChevronRightIcon sx={{ color: "#A59D96" }} />

                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* ➕ ADD RIDER DIALOG */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "24px", p: 1 } }}>
        <DialogTitle sx={{ fontWeight: "900", color: "#3A2C23" }}>Add New Rider</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" fullWidth value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
            <TextField label="Email" fullWidth value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
            <TextField label="Phone" fullWidth value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />

            {/* 👇 Hides the input if we already know which zone we are in */}
            {!activeAdminId && (
              <TextField label="Zone Name" fullWidth value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
            )}

            <TextField label="Password" type="password" fullWidth value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenAdd(false)} sx={{ color: "#6E655E", fontWeight: "800" }}>Cancel</Button>
          <Button variant="contained" disableElevation onClick={handleAddRider} disabled={actionLoading} sx={{ borderRadius: "12px", bgcolor: "#F8C332", color: "#3A2C23", fontWeight: "900" }}>
            {actionLoading ? <CircularProgress size={24} /> : "Create Rider"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: '12px', fontWeight: 'bold' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}